/**
 * CrudResourceRegistry - Централізований реєстр контекстів CRUD ресурсів
 *
 * Проблема:
 * - Кожен компонент (ResourceList, ResourceDetail) створює свій окремий CrudResourceStore
 * - Неможливо синхронізувати стан між списком та карточкою
 * - Дублювання контекстів для одного і того ж ресурсу
 *
 * Рішення:
 * - Єдиний контекст для списку ресурсу (list context)
 * - Окремі контексти для кожної карточки (detail contexts)
 * - Можливість отримати контекст списку з карточки для перезавантаження
 *
 * Структура ключів:
 * - List: "resourceName:list"
 * - Detail: "resourceName:itemId:mode" або "resourceName:create" для нових
 *
 * @example
 * ```typescript
 * // Отримання або створення контексту списку
 * const listContext = crudResourceRegistry.getOrCreateListContext('users', {...props})
 *
 * // Отримання або створення контексту карточки
 * const detailContext = crudResourceRegistry.getOrCreateDetailContext(
 *   'users',
 *   { mode: 'edit', itemId: 123, ...props }
 * )
 *
 * // Перезавантаження списку з карточки
 * const listContext = crudResourceRegistry.getListContext('users')
 * if (listContext?.actions?.reload) {
 *   listContext.actions.reload()
 * }
 *
 * // Очищення контекстів карточок при закритті
 * crudResourceRegistry.clearDetailContext('users', 123, 'edit')
 * ```
 */

import type { CrudResourceStore } from '@/Core/UI/CrudResource/Stores/CrudResourceStore'
import { action, makeObservable } from 'mobx'

interface DetailContextKey {
    resourceName: string
    itemId?: string | number
    mode: 'create' | 'view' | 'edit'
}

export class CrudResourceRegistry {
    // Контексти списків: Map<"resourceName:list", CrudResourceStore>
    private listContexts = new Map<string, CrudResourceStore>()

    // Контексти карточок: Map<"resourceName:itemId:mode", CrudResourceStore>
    private detailContexts = new Map<string, CrudResourceStore>()

    private debug: boolean

    constructor(options: { debug?: boolean } = {}) {
        this.debug = options.debug ?? false

        makeObservable(this, {
            setListContext: action,
            setDetailContext: action,
            clearListContext: action,
            clearDetailContext: action,
            clearAllDetailContexts: action,
            clearAll: action,
        })
    }

    /**
     * Генерує ключ для контексту списку
     */
    private getListKey(resourceName: string): string {
        if (!resourceName || typeof resourceName !== 'string') {
            throw new Error('[CrudResourceRegistry] Invalid resourceName: must be a non-empty string')
        }
        return `${resourceName}:list`
    }

    /**
     * Генерує ключ для контексту карточки
     */
    private getDetailKey(params: DetailContextKey): string {
        const { resourceName, itemId, mode } = params

        if (!resourceName || typeof resourceName !== 'string') {
            throw new Error('[CrudResourceRegistry] Invalid resourceName: must be a non-empty string')
        }

        if (!mode || !['create', 'view', 'edit'].includes(mode)) {
            throw new Error('[CrudResourceRegistry] Invalid mode: must be "create", "view", or "edit"')
        }

        if (mode === 'create') {
            return `${resourceName}:create`
        }

        if (!itemId) {
            throw new Error(`[CrudResourceRegistry] itemId is required for mode: ${mode}`)
        }

        return `${resourceName}:${itemId}:${mode}`
    }

    /**
     * Отримує контекст списку
     */
    getListContext(resourceName: string): CrudResourceStore | undefined {
        const key = this.getListKey(resourceName)
        return this.listContexts.get(key)
    }

    /**
     * Отримує або створює контекст списку
     *
     * @param resourceName - назва ресурсу
     * @param createContextFn - функція створення контексту (викликається тільки якщо контекст не існує)
     * @param forceRecreate - примусово пересоздати контекст
     */
    getOrCreateListContext(
        resourceName: string,
        createContextFn: () => CrudResourceStore,
        forceRecreate: boolean = false
    ): CrudResourceStore {
        const key = this.getListKey(resourceName)

        if (forceRecreate) {
            this.clearListContext(resourceName)
        }

        let context = this.listContexts.get(key)

        if (!context) {

            context = createContextFn()
            this.listContexts.set(key, context)
        }

        return context
    }

    /**
     * Встановлює контекст списку
     */
    setListContext(resourceName: string, context: CrudResourceStore): void {
        const key = this.getListKey(resourceName)
        this.listContexts.set(key, context)
    }

    /**
     * Очищає контекст списку
     */
    clearListContext(resourceName: string): boolean {
        const key = this.getListKey(resourceName)
        const result = this.listContexts.delete(key)

        return result
    }

    /**
     * Отримує контекст карточки
     */
    getDetailContext(params: DetailContextKey): CrudResourceStore | undefined {
        const key = this.getDetailKey(params)
        return this.detailContexts.get(key)
    }

    /**
     * Отримує або створює контекст карточки
     *
     * @param params - параметри карточки (resourceName, itemId, mode)
     * @param createContextFn - функція створення контексту
     * @param forceRecreate - примусово пересоздати контекст
     */
    getOrCreateDetailContext(
        params: DetailContextKey,
        createContextFn: () => CrudResourceStore,
        forceRecreate: boolean = false
    ): CrudResourceStore {
        const key = this.getDetailKey(params)

        if (forceRecreate) {
            this.clearDetailContext(params)
        }

        let context = this.detailContexts.get(key)

        if (!context) {

            context = createContextFn()
            this.detailContexts.set(key, context)
        }

        return context
    }

    /**
     * Встановлює контекст карточки
     */
    setDetailContext(params: DetailContextKey, context: CrudResourceStore): void {
        const key = this.getDetailKey(params)
        this.detailContexts.set(key, context)
    }

    /**
     * Очищає контекст карточки
     */
    clearDetailContext(params: DetailContextKey): boolean {
        const key = this.getDetailKey(params)
        const context = this.detailContexts.get(key)
        
        // Викликаємо dispose якщо є
        if (context && typeof (context as any).dispose === 'function') {
            ;(context as any).dispose()
        }
        
        const result = this.detailContexts.delete(key)

        return result
    }

    /**
     * Очищає всі контексти карточок для ресурсу
     */
    clearAllDetailContexts(resourceName: string): number {
        let cleared = 0

        for (const key of this.detailContexts.keys()) {
            if (key.startsWith(`${resourceName}:`)) {
                this.detailContexts.delete(key)
                cleared++
            }
        }

        return cleared
    }

    /**
     * Очищає всі контексти
     */
    clearAll(): void {
        // Викликаємо dispose для всіх контекстів
        this.listContexts.forEach((context) => {
            if (typeof (context as any).dispose === 'function') {
                ;(context as any).dispose()
            }
        })
        
        this.detailContexts.forEach((context) => {
            if (typeof (context as any).dispose === 'function') {
                ;(context as any).dispose()
            }
        })
        
        this.listContexts.clear()
        this.detailContexts.clear()
    }

    /**
     * Отримує всі активні контексти для ресурсу
     * Корисно для дебагу або для масових операцій
     */
    getResourceContexts(resourceName: string): {
        list?: CrudResourceStore
        details: Array<{
            key: string
            context: CrudResourceStore
            mode: string
            itemId?: string | number
        }>
    } {
        const result: any = {
            details: [],
        }

        // Додаємо контекст списку якщо є
        const listContext = this.getListContext(resourceName)
        if (listContext) {
            result.list = listContext
        }

        // Додаємо всі контексти карточок
        for (const [key, context] of this.detailContexts.entries()) {
            if (key.startsWith(`${resourceName}:`)) {
                const parts = key.split(':')
                result.details.push({
                    key,
                    context,
                    mode: parts[parts.length - 1],
                    itemId: parts.length === 3 ? parts[1] : undefined,
                })
            }
        }

        return result
    }

    /**
     * Перевіряє чи є активні контексти для ресурсу
     */
    hasActiveContexts(resourceName: string): boolean {
        if (this.getListContext(resourceName)) {
            return true
        }

        for (const key of this.detailContexts.keys()) {
            if (key.startsWith(`${resourceName}:`)) {
                return true
            }
        }

        return false
    }

    /**
     * Оновлює контекст списку безпечно
     * Викликає callback з існуючим контекстом, якщо він є
     * 
     * @example
     * ```typescript
     * crudResourceRegistry.updateListContext('users', (context) => {
     *   context.filterFormContext?.setFieldValue('status', 'active')
     * })
     * ```
     */
    updateListContext(resourceName: string, updateFn: (context: CrudResourceStore) => void): boolean {
        const context = this.getListContext(resourceName)
        
        if (!context) {
            if (this.debug) {
                console.warn(`[CrudResourceRegistry] Cannot update - list context not found for: ${resourceName}`)
            }
            return false
        }

        try {
            updateFn(context)
            return true
        } catch (error) {
            if (this.debug) {
                console.error(`[CrudResourceRegistry] Error updating list context for ${resourceName}:`, error)
            }
            return false
        }
    }

    /**
     * Оновлює контекст карточки безпечно
     */
    updateDetailContext(
        params: DetailContextKey,
        updateFn: (context: CrudResourceStore) => void
    ): boolean {
        const context = this.getDetailContext(params)
        
        if (!context) {
            if (this.debug) {
                const key = this.getDetailKey(params)
                console.warn(`[CrudResourceRegistry] Cannot update - detail context not found: ${key}`)
            }
            return false
        }

        try {
            updateFn(context)
            return true
        } catch (error) {
            if (this.debug) {
                const key = this.getDetailKey(params)
                console.error(`[CrudResourceRegistry] Error updating detail context ${key}:`, error)
            }
            return false
        }
    }

    /**
     * Отримує статистику по контекстам
     */
    getStats(): {
        listContextsCount: number
        detailContextsCount: number
        listKeys: string[]
        detailKeys: string[]
    } {
        return {
            listContextsCount: this.listContexts.size,
            detailContextsCount: this.detailContexts.size,
            listKeys: Array.from(this.listContexts.keys()),
            detailKeys: Array.from(this.detailContexts.keys()),
        }
    }

    /**
     * Перевіряє чи існує контекст списку для ресурсу
     */
    hasListContext(resourceName: string): boolean {
        const key = this.getListKey(resourceName)
        return this.listContexts.has(key)
    }

    /**
     * Перезавантажує список для ресурсу (якщо контекст існує)
     * Корисно викликати після збереження в карточці
     */
    reloadList(resourceName: string): boolean {
        const listContext = this.getListContext(resourceName)

        if (!listContext) {
            if (this.debug) {
                console.warn(`[CrudResourceRegistry] Cannot reload - list context not found for: ${resourceName}`)
            }
            return false
        }

        const reloadAction = (listContext.actions as any)?.reload
        if (typeof reloadAction === 'function') {
            reloadAction()
            return true
        }

        if (this.debug) {
            console.warn(`[CrudResourceRegistry] List context for ${resourceName} has no reload action`)
        }

        return false
    }

    /**
     * Тихо перезавантажує список якщо контекст існує (без попереджень)
     * Використовуйте цей метод, коли невідомо чи є список відкритим
     */
    reloadListIfExists(resourceName: string): boolean {
        console.log('[CrudResourceRegistry] reloadListIfExists called for:', resourceName, new Error().stack?.split('\n').slice(1, 5).join(' | '))
        const listContext = this.getListContext(resourceName)

        if (!listContext) {
            console.log('[CrudResourceRegistry] reloadListIfExists - no listContext found for:', resourceName)
            return false
        }

        const reloadAction = (listContext.actions as any)?.reload
        
        if (typeof reloadAction === 'function') {
            try {
                console.log('[CrudResourceRegistry] reloadListIfExists - calling reload() for:', resourceName)
                reloadAction()
                return true
            } catch (error) {
                console.error('[CrudResourceRegistry] Reload action error:', error)
                return false
            }
        }

        console.warn('[CrudResourceRegistry] Reload action not available for', resourceName)
        return false
    }
}

// Singleton instance
export const crudResourceRegistry = new CrudResourceRegistry({
    debug: import.meta.env.DEV, // Включаємо debug тільки в dev режимі
})
